import { z } from 'zod';
import {
  AllCharLabelSchema,
  CommaSeparatedTagsSchema,
  HumanFullNameSchema,
  LocationSchema,
  SlackChannelIdSchema,
  SlackUserIdSchema,
  StringBooleanSchema,
  SubjectSchema,
  TagSchema,
  UnixTimestampSchema,
} from '../common/schemas';
import { DBCourseCourseIdSchema } from '../courses/schemas';
import { PermissionGroupGroupIdSchema } from '../permissionGroups/schemas';
import { RoomRoomIdSchema } from '../rooms/schemas';
import {
  DBSchoolLabelSchema,
  DBSchoolSchoolIdSchema,
  DBSchoolShortLabelSchema,
} from '../schools/schemas';
import { DBStaffStaffIdSchema } from '../staff/schemas';
import { DBStreamStreamIdSchema } from '../streams/schemas';
import { StudentStudentIdSchema } from '../students/schemas';
import { DESIRED_CLASS_SIZE, MAX_CLASS_SIZE } from './constants';

/**
 * Shared & Helper Schemas
 */

export const DBClassSubjectClassIdSchema = z
  .string()
  .refine(val => {
    const parts = val.split('#');

    return (
      parts.length === 2 &&
      SubjectSchema.safeParse(parts[0]).success &&
      DBClassClassIdSchema.safeParse(parts[1]).success
    );
  })
  .openapi({
    description: 'A composite key combining subject and class ID, formatted as "subject#classId"',
    example: 'chem#potassium',
  });
export const DBClassStudentsEntrySchema = z.object({
  name: HumanFullNameSchema,
  schoolId: z.lazy(() => DBSchoolSchoolIdSchema).optional(),
  schoolLabel: z.lazy(() => DBSchoolLabelSchema).optional(),
  schoolShortLabel: z.lazy(() => DBSchoolShortLabelSchema).optional(),
  slackId: SlackUserIdSchema.optional(),
});
export const ClassTagSchema = TagSchema;
export const DBClassUnassignableStaffIdSchema = z.lazy(() =>
  z.union([DBStaffStaffIdSchema, z.literal('')])
);
export const DBClassUnassignableStaffNameSchema = z.union([HumanFullNameSchema, z.literal('')]);

/**
 * DB Schemas
 */

export const DBClassSubjectSchema = SubjectSchema;
export const DBClassClassIdSchema = z
  .string()
  .regex(/^[a-z0-9]+$/)
  .min(1)
  .max(64);
export const DBClassActiveSchema = z.boolean();
export const DBClassActiveDayTimeSortKeySchema = z.string().refine(val => {
  const parts = val.split('#');

  return (
    parts.length === 3 &&
    StringBooleanSchema.safeParse(parts[0]).success &&
    DBClassDaySchema.safeParse(Number(parts[1])).success &&
    DBClassTimeSchema.safeParse(parts[2]).success
  );
});
export const DBClassActiveStreamSortKeySchema = z.string().refine(val => {
  const parts = val.split('#');
  return (
    parts.length === 3 &&
    StringBooleanSchema.safeParse(parts[0]).success &&
    z.lazy(() => DBCourseCourseIdSchema).safeParse(parts[1]).success &&
    z.lazy(() => DBStreamStreamIdSchema).safeParse(parts[2]).success
  );
});
export const DBClassAllowMakeupsSchema = z.boolean();
export const DBClassCourseSchema = z.lazy(() => DBCourseCourseIdSchema);
export const DBClassDaySchema = z.number().int().min(-1).max(6);
export const DBClassLocationSchema = LocationSchema;
export const DBClassMarkerSchema = DBClassUnassignableStaffIdSchema.openapi({
  description: 'Staff ID of the marker allocated for the class',
  example: '550e8400-e29b-41d4-a716-446655440000',
});
export const DBClassMarkerNameSchema = DBClassUnassignableStaffNameSchema;
export const DBClassNotesSchema = z.string().min(0).max(4096);
export const DBClassPermissionGroupSchema = z.lazy(() => PermissionGroupGroupIdSchema);
export const DBClassRemindBreaksSchema = z.boolean();
export const DBClassRoomSchema = z.lazy(() => RoomRoomIdSchema);
export const DBClassSchoolTagSchema = AllCharLabelSchema.min(0).max(256);
export const DBClassSlackChannelNameSchema = z.string().refine(val => {
  // Legacy Slack channels use `_` as the separator; current channels use `-`.
  // Both forms are semantically identical and the underscore form still refers
  // to real channels, so both are accepted.
  const parts = val.split(/[-_]/);
  return (
    parts.length === 3 &&
    parts[0] === 'cl' &&
    z.lazy(() => DBCourseCourseIdSchema).safeParse(parts[1]).success &&
    DBClassClassIdSchema.safeParse(parts[2]).success
  );
});
export const DBClassSlackIdSchema = SlackChannelIdSchema;
export const DBClassStreamSchema = z.lazy(() => DBStreamStreamIdSchema);
export const DBClassStreamsSchema = z.record(
  z.lazy(() => DBCourseCourseIdSchema),
  z.lazy(() => DBStreamStreamIdSchema)
);
export const DBClassStudentCapSchema = z.number().int().min(0).max(MAX_CLASS_SIZE);
export const DBClassStudentCountSchema = z.number().int().min(0).max(MAX_CLASS_SIZE);
export const DBClassStudentsSchema = z.record(
  z.lazy(() => StudentStudentIdSchema),
  DBClassStudentsEntrySchema
);
export const DBClassTagsSchema = CommaSeparatedTagsSchema;
export const DBClassTeacherSchema = DBClassUnassignableStaffIdSchema.openapi({
  description: 'Staff ID of the teacher allocated for the class',
  example: '550e8400-e29b-41d4-a716-446655440000',
});
export const DBClassTeacherNameSchema = DBClassUnassignableStaffNameSchema;
export const DBClassTimeSchema = z.string().regex(/^(0[0-9]|1[0-9]|2[0-4])[0-5][0-9]$/);
export const DBClassWaitlistSchema = z.array(
  z.object({
    studentId: z.lazy(() => StudentStudentIdSchema),
    name: HumanFullNameSchema,
    schoolLabel: z.lazy(() => DBSchoolLabelSchema).optional(),
    schoolShortLabel: z.lazy(() => DBSchoolShortLabelSchema).optional(),
    addedDatetime: UnixTimestampSchema,
  })
);

export const DBClassSchema = z.object({
  subject: DBClassSubjectSchema,
  classId: DBClassClassIdSchema,
  active: DBClassActiveSchema,
  activeDayTimeSortKey: DBClassActiveDayTimeSortKeySchema,
  activeStreamSortKey: DBClassActiveStreamSortKeySchema,
  allowMakeups: DBClassAllowMakeupsSchema,
  course: DBClassCourseSchema,
  day: DBClassDaySchema,
  location: DBClassLocationSchema,
  marker: DBClassMarkerSchema,
  markerName: DBClassMarkerNameSchema.optional(),
  notes: DBClassNotesSchema,
  permissionGroup: DBClassPermissionGroupSchema,
  remindBreaks: DBClassRemindBreaksSchema,
  room: DBClassRoomSchema,
  schoolTag: DBClassSchoolTagSchema,
  slackChannelName: DBClassSlackChannelNameSchema,
  slackId: DBClassSlackIdSchema,
  stream: DBClassStreamSchema,
  // The stream in use for each course the class has run, keyed by course; `course` selects
  // the entry in use, which the codec keeps equal to `stream`
  streams: DBClassStreamsSchema.optional(),
  studentCap: DBClassStudentCapSchema,
  studentCount: DBClassStudentCountSchema,
  // Optional to allow removal of a specific student
  students: DBClassStudentsSchema.optional(),
  tags: DBClassTagsSchema,
  teacher: DBClassTeacherSchema,
  teacherName: DBClassTeacherNameSchema.optional(),
  time: DBClassTimeSchema,
  waitlist: DBClassWaitlistSchema,
});

/**
 * Client Schemas
 */

export const ClassSubjectSchema = DBClassSubjectSchema;
export const ClassClassIdSchema = DBClassClassIdSchema;
export const ClassActiveSchema = DBClassActiveSchema;
export const ClassAllowMakeupsSchema = DBClassAllowMakeupsSchema;
export const ClassCourseSchema = DBClassCourseSchema;
export const ClassDaySchema = DBClassDaySchema;
export const ClassLocationSchema = DBClassLocationSchema;
export const ClassMarkerSchema = DBClassMarkerSchema;
export const ClassMarkerNameSchema = DBClassMarkerNameSchema;
export const ClassNotesSchema = DBClassNotesSchema;
export const ClassPermissionGroupSchema = DBClassPermissionGroupSchema;
export const ClassRemindBreaksSchema = DBClassRemindBreaksSchema;
export const ClassRoomSchema = DBClassRoomSchema;
export const ClassSchoolTagSchema = DBClassSchoolTagSchema;
export const ClassSlackChannelNameSchema = DBClassSlackChannelNameSchema;
export const ClassSlackIdSchema = DBClassSlackIdSchema;
export const ClassStreamSchema = DBClassStreamSchema;
export const ClassStreamsSchema = DBClassStreamsSchema;
export const ClassStudentCapSchema = DBClassStudentCapSchema;
export const ClassStudentCountSchema = DBClassStudentCountSchema;
export const ClassStudentsSchema = z.array(
  z.object({
    studentId: z.lazy(() => StudentStudentIdSchema),
    name: HumanFullNameSchema,
    schoolId: z.lazy(() => DBSchoolSchoolIdSchema).optional(),
    schoolLabel: z.lazy(() => DBSchoolLabelSchema).optional(),
    schoolShortLabel: z.lazy(() => DBSchoolShortLabelSchema).optional(),
    slackId: SlackUserIdSchema.optional(),
  })
);
export const ClassTagsSchema = DBClassTagsSchema;
export const ClassTeacherSchema = DBClassTeacherSchema;
export const ClassTeacherNameSchema = DBClassTeacherNameSchema;
export const ClassTimeSchema = DBClassTimeSchema;
export const ClassWaitlistSchema = DBClassWaitlistSchema;

export const ClassSchema = z.object({
  subject: ClassSubjectSchema,
  classId: ClassClassIdSchema,
  active: ClassActiveSchema.default(true),
  allowMakeups: ClassAllowMakeupsSchema.default(true),
  course: ClassCourseSchema,
  day: ClassDaySchema,
  location: ClassLocationSchema,
  marker: ClassMarkerSchema.default(''),
  markerName: ClassMarkerNameSchema.default(''),
  notes: ClassNotesSchema.default(''),
  permissionGroup: ClassPermissionGroupSchema,
  remindBreaks: ClassRemindBreaksSchema.default(false),
  room: ClassRoomSchema,
  schoolTag: ClassSchoolTagSchema.default(''),
  slackChannelName: ClassSlackChannelNameSchema,
  slackId: ClassSlackIdSchema,
  stream: ClassStreamSchema,
  streams: ClassStreamsSchema.optional(),
  studentCap: ClassStudentCapSchema.default(DESIRED_CLASS_SIZE),
  studentCount: ClassStudentCountSchema.default(0),
  students: ClassStudentsSchema.default([]),
  tags: ClassTagsSchema.default(''),
  teacher: ClassTeacherSchema.default(''),
  teacherName: ClassTeacherNameSchema.default(''),
  time: ClassTimeSchema.default('0000'),
  waitlist: ClassWaitlistSchema.default([]),
});
